import { useMemo } from "react";
import { Phone, Mail, User as UserIcon, Tag, Flame, Snowflake, ThermometerSun, Globe } from "lucide-react";

const SCORE_BADGES = {
  hot: { Icon: Flame, label: "HOT", color: "#dc2626", bg: "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400" },
  warm: { Icon: ThermometerSun, label: "WARM", color: "#f59e0b", bg: "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400" },
  cold: { Icon: Snowflake, label: "COLD", color: "#64748b", bg: "bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
};

/**
 * Read-only Kanban (Stages werden automatisch durch Aktionen ausgelöst).
 * Drag&Drop wurde entfernt — Karten sind klickbar (öffnen Detail-Modal),
 * Stage-Wechsel passieren via Anruf, E-Mail, Demo, Buchung, Inquiry, Angebot.
 */
export default function PipelineKanban({ stages, leads, users, onCardClick, filterUserId, selectedIds = new Set(), onToggleSelect, bulkMode = false }) {
  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  const leadsByStage = useMemo(() => {
    const filtered = filterUserId === "_mine"
      ? leads.filter(l => l.assigned_to === filterUserId)
      : filterUserId && filterUserId !== "_all"
        ? leads.filter(l => l.assigned_to === filterUserId)
        : leads;
    const map = {};
    stages.forEach(s => { map[s.id] = []; });
    filtered.forEach(l => { if (map[l.stage_id]) map[l.stage_id].push(l); });
    return map;
  }, [leads, stages, filterUserId]);

  const totals = useMemo(() => {
    const out = {};
    stages.forEach(s => {
      const ls = leadsByStage[s.id] || [];
      out[s.id] = { count: ls.length };
    });
    return out;
  }, [leadsByStage, stages]);

  return (
    <div className="space-y-3">
      <div className="flex gap-3 overflow-x-auto pb-3" data-testid="pipeline-kanban">
        {stages.map(stage => {
          const ls = leadsByStage[stage.id] || [];
          const t = totals[stage.id];
          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-72 bg-muted/30 border border-border rounded-sm flex flex-col"
              data-testid={`stage-col-${stage.id}`}
            >
              {/* Stage Header */}
              <div className="px-3 py-2.5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10 rounded-t-sm" style={{ borderTop: `3px solid ${stage.color}` }}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: stage.color }} />
                  <span className="text-xs font-bold uppercase tracking-widest truncate">{stage.name}</span>
                  <span className="text-[10px] font-mono text-muted-foreground">{t.count}</span>
                </div>
              </div>
              {/* Cards */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[160px]">
                {ls.length === 0 && (
                  <p className="text-[11px] text-muted-foreground text-center py-6 italic">Keine Leads in dieser Stage</p>
                )}
                {ls.map(l => {
                  const owner = userById[l.assigned_to];
                  const cat = l.score_category || "cold";
                  const badge = SCORE_BADGES[cat] || SCORE_BADGES.cold;
                  const isSelected = selectedIds.has(l.id);
                  return (
                    <div
                      key={l.id}
                      onClick={() => bulkMode ? onToggleSelect?.(l.id) : onCardClick(l.id)}
                      className={`bg-card border rounded-sm p-3 hover:border-foreground hover:shadow-sm transition-all cursor-pointer ${isSelected ? "border-foreground ring-2 ring-foreground/30" : "border-border"}`}
                      data-testid={`lead-card-${l.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="text-sm font-bold text-foreground truncate flex-1 min-w-0">{l.company_name}</p>
                        {bulkMode && (
                          <input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); onToggleSelect?.(l.id); }} onClick={(e) => e.stopPropagation()} className="mt-0.5 accent-foreground shrink-0" data-testid={`select-${l.id}`} />
                        )}
                        {l.score != null && !bulkMode && (
                          <span
                            className={`shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded ${badge.bg}`}
                            title={`Score ${l.score}/100 · ${
                              l.score_breakdown?.website_note
                                ? `Website: ${l.score_breakdown.website_note}`
                                : l.website ? "Website vorhanden" : "Keine Website"
                            }`}
                          >
                            <badge.Icon size={9} /> {l.score}
                          </span>
                        )}
                      </div>
                      {l.contact_name && <p className="text-xs text-muted-foreground truncate">{l.contact_name}</p>}
                      {(l.city || l.address) && <p className="text-[10px] text-muted-foreground truncate mt-0.5">{[l.address, l.city].filter(Boolean).join(", ")}</p>}
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        {l.contact_phone && <a href={`tel:${l.contact_phone}`} onClick={e => e.stopPropagation()} className="text-[10px] text-muted-foreground font-mono inline-flex items-center gap-0.5 hover:text-foreground"><Phone size={10} /> Anrufen</a>}
                        {l.contact_email && <a href={`mailto:${l.contact_email}`} onClick={e => e.stopPropagation()} className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5 hover:text-foreground"><Mail size={10} /></a>}
                        {l.website && <a href={l.website.startsWith("http") ? l.website : `https://${l.website}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5 hover:text-foreground" title={l.website}><Globe size={10} /></a>}
                      </div>
                      <div className="flex items-center justify-between mt-2.5 gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {owner ? (
                            <span className="w-5 h-5 rounded-full bg-foreground text-background flex items-center justify-center text-[9px] font-bold shrink-0" title={owner.name || owner.email}>
                              {(owner.name || owner.email)[0].toUpperCase()}
                            </span>
                          ) : (
                            <span className="w-5 h-5 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-[9px] shrink-0" title="Nicht zugewiesen">
                              <UserIcon size={9} />
                            </span>
                          )}
                          {l.tags && l.tags.length > 0 && (
                            <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-muted text-muted-foreground inline-flex items-center gap-0.5"><Tag size={8} /> {l.tags[0]}{l.tags.length > 1 ? `+${l.tags.length-1}` : ""}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
